import React, { useState, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import SmileScoreDisplay from './SmileScoreDisplay';
import TreatmentSummaryDisplay from './TreatmentSummaryDisplay';
import ProTreatmentSummaryDisplay from './ProTreatmentSummaryDisplay';
import ClinicSettingsModal from './ClinicSettingsModal';
import DownloadIcon from './icons/DownloadIcon';
import Wand2Icon from './icons/Wand2Icon';
import ImageUpIcon from './icons/ImageUpIcon';
import FilmIcon from './icons/FilmIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import ClipboardIcon from './icons/ClipboardIcon';
import SparklesIcon from './icons/SparklesIcon';
import EyeIcon from './icons/EyeIcon';
import Trash2Icon from './icons/Trash2Icon';
import LassoSelectIcon from './icons/LassoSelectIcon';
import CameraIcon from './icons/CameraIcon';
import ScissorsIcon from './icons/ScissorsIcon';
import FileTextIcon from './icons/FileTextIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';
import BookOpenIcon from './icons/BookOpenIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import IterationCcwIcon from './icons/IterationCcwIcon';
import CrownIcon from './icons/CrownIcon';
import Settings2Icon from './icons/Settings2Icon';

interface StudioPanelProps {
    onGoBack: () => void;
    onReset: () => void;
    onGenerateVideo: (aspectRatio: string) => void;
    generatedVideoUrl: string | null;
    videoError: string | null;
    onClearVideo: () => void;
    treatmentPlan: object | null;
    generatedText: string | null;
    smileRating: object | null;
    afterSmileRating: object | null;
    onStartDentureCrop: () => void;
    extractedDentureUrl: string | null;
    extractionError: string | null;
    onClearExtractedDenture: () => void;
    onStartMaskCreation: () => void;
    onOpenExtractionViewer: () => void;
    onGenerateShowcasePortrait: () => void;
    portraitError: string | null;
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
    onViewPlan: () => void;
    onShowcaseResult: () => void;
    onGenerateReport: () => void;
    isGeneratingReport: boolean;
    reportError: string | null;
    onGeneratePrestigeReport: () => void;
    isGeneratingPrestigeReport: boolean;
    prestigeReportError: string | null;
    onGenerateEliteReport: () => void;
    isGeneratingEliteReport: boolean;
    eliteReportError: string | null;
    onGenerateBrochure: () => void;
    isGeneratingBrochure: boolean;
    brochureError: string | null;
    onDownloadImage: () => void;
    onDownloadVideo: () => void;
    onOpenRefineModal: () => void;
}

const StudioPanel: React.FC<StudioPanelProps> = (props) => {
    const [openSection, setOpenSection] = useState('analysis');
    const [isClinicSettingsOpen, setIsClinicSettingsOpen] = useState(false);
    const [selectedVideoRatio, setSelectedVideoRatio] = useState<'16:9' | '9:16'>('16:9');

    const AccordionSection: React.FC<{ title: string; id: string; children: ReactNode }> = ({ title, id, children }) => (
        <div className="py-4 border-b border-white/10 last:border-b-0">
            <button onClick={() => setOpenSection(openSection === id ? '' : id)} className="w-full flex justify-between items-center">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">{title}</h3>
                <motion.div animate={{ rotate: openSection === id ? 180 : 0 }}>
                    <ChevronDownIcon className="w-5 h-5 text-zinc-500" />
                </motion.div>
            </button>
            <AnimatePresence initial={false}>
                {openSection === id && (
                    <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { opacity: 1, height: 'auto', marginTop: '16px' },
                            collapsed: { opacity: 0, height: 0, marginTop: '0px' }
                        }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    type ActionButtonVariant = 'primary' | 'secondary' | 'tertiary';
    const ActionButton: React.FC<{
        onClick: () => void;
        children: React.ReactNode;
        variant?: ActionButtonVariant;
        icon: React.ReactNode;
        disabled?: boolean;
        title?: string;
    }> = ({ onClick, children, variant = 'secondary', icon, disabled = false, title }) => {
        const baseClasses = "w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 border disabled:opacity-50 disabled:cursor-not-allowed";
        const variantClasses = {
            primary: 'bg-gradient-to-br from-teal-400 to-teal-600 text-white border-transparent shadow-lg shadow-teal-500/20 hover:from-teal-500 hover:to-teal-600',
            secondary: 'bg-white/10 text-zinc-200 border-white/10 hover:bg-white/20',
            tertiary: 'bg-transparent text-zinc-400 border-transparent hover:bg-white/5 hover:text-zinc-200 justify-start',
        };

        return (
            <motion.button
                onClick={onClick}
                disabled={disabled}
                title={title}
                whileHover={disabled ? {} : { scale: 1.03 }}
                whileTap={disabled ? {} : { scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                className={`${baseClasses} ${variantClasses[variant]}`}
            >
                {icon}
                <span>{children}</span>
            </motion.button>
        );
    };

    return (
        <aside className="w-96 flex-shrink-0 bg-zinc-950/60 backdrop-blur-2xl border-l border-white/10 flex flex-col h-full">
            <div className="p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <SparklesIcon className="w-6 h-6 text-teal-400" />
                    <span>Studio Panel</span>
                </h2>
            </div>
            <div className="flex-1 overflow-y-auto px-6">
                <AccordionSection title="Smile Analysis" id="analysis">
                    <div className="space-y-4">
                        <SmileScoreDisplay ratingData={props.smileRating} afterRatingData={props.afterSmileRating} />
                        {props.treatmentType === 'pro-automated' && props.treatmentPlan && (
                            <ProTreatmentSummaryDisplay plan={props.treatmentPlan} />
                        )}
                        {props.treatmentType !== 'pro-automated' && (props.treatmentPlan || props.generatedText) && (
                            <div className="space-y-3">
                                <TreatmentSummaryDisplay plan={props.treatmentPlan} genText={props.generatedText} />
                            </div>
                        )}

                    </div>
                </AccordionSection>

                <AccordionSection title="Smile Refinement" id="iterative">
                    <ActionButton
                        onClick={props.onOpenRefineModal}
                        variant="primary"
                        icon={<IterationCcwIcon className="w-4 h-4" />}
                    >
                        Refine This Design
                    </ActionButton>
                </AccordionSection>

                <AccordionSection title="Design Tools" id="creative">
                    <div className="space-y-4">
                        <h4 className="text-xs font-semibold text-zinc-500">Advanced Editing</h4>
                        {props.extractedDentureUrl ? (
                            <div className="space-y-3">
                                <div className="p-3 bg-teal-950/50 border border-teal-500/30 rounded-lg text-center"><p className="text-sm font-semibold text-teal-300">Model Extracted</p></div>
                                <ActionButton onClick={props.onStartMaskCreation} variant="primary" icon={<LassoSelectIcon className="w-4 h-4" />}>Open Virtual Try-On</ActionButton>
                                <div className="flex items-center gap-2 pt-2 border-t border-white/10 mt-2">
                                    <ActionButton onClick={props.onOpenExtractionViewer} variant="secondary" icon={<EyeIcon className="w-4 h-4" />}>Preview Model</ActionButton>
                                    <motion.button onClick={props.onClearExtractedDenture} title="Delete Dental Model" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="flex-shrink-0 p-3 rounded-lg bg-red-900/40 text-red-400 border border-red-500/30 hover:bg-red-900/80"><Trash2Icon className="w-4 h-4" /></motion.button>
                                </div>
                            </div>
                        ) : null}
                        {props.extractionError && <p className="text-red-500 text-xs mt-2 text-center">{props.extractionError}</p>}
                        <div className="pt-4 mt-2 border-t border-white/10">
                            <h4 className="text-xs font-semibold text-zinc-500 mb-2">Advanced Visuals</h4>
                            {props.generatedVideoUrl ? (
                                <ActionButton onClick={props.onClearVideo} icon={<ArrowLeftIcon className="w-4 h-4" />}>Return to Image</ActionButton>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex gap-2 mb-2">
                                        <button
                                            onClick={() => setSelectedVideoRatio('16:9')}
                                            className={`flex-1 py-2 px-3 text-xs rounded-lg border transition-all ${selectedVideoRatio === '16:9' ? 'bg-teal-950/50 border-teal-500/50 text-teal-300' : 'bg-white/5 border-white/10 text-zinc-400 hover:bg-white/10'}`}
                                        >
                                            Horizontal (16:9)
                                        </button>
                                        <button
                                            onClick={() => setSelectedVideoRatio('9:16')}
                                            className={`flex-1 py-2 px-3 text-xs rounded-lg border transition-all ${selectedVideoRatio === '9:16' ? 'bg-teal-950/50 border-teal-500/50 text-teal-300' : 'bg-white/5 border-white/10 text-zinc-400 hover:bg-white/10'}`}
                                        >
                                            Vertical (9:16)
                                        </button>
                                    </div>
                                    <ActionButton onClick={() => props.onGenerateVideo(selectedVideoRatio)} variant="secondary" icon={<FilmIcon className="w-4 h-4" />}>Create Animation</ActionButton>
                                </div>
                            )}
                            {props.videoError && <p className="text-red-500 text-xs mt-2 text-center">{props.videoError}</p>}
                            <ActionButton onClick={props.onGenerateShowcasePortrait} variant="secondary" icon={<CameraIcon className="w-4 h-4" />}>Create Portrait</ActionButton>
                            {props.portraitError && <p className="text-red-500 text-xs mt-2 text-center">{props.portraitError}</p>}
                        </div>
                    </div>
                </AccordionSection>

                <AccordionSection title="Downloads & Reports" id="exports">
                    <div className="space-y-3">
                        <ActionButton onClick={props.onDownloadImage} variant="secondary" icon={<DownloadIcon className="w-4 h-4" />}>Save Image</ActionButton>
                        <ActionButton onClick={props.onDownloadVideo} variant="secondary" icon={<DownloadIcon className="w-4 h-4" />} disabled={!props.generatedVideoUrl}>Save Video</ActionButton>
                        <div className="pt-3 mt-2 border-t border-white/10 space-y-3">
                            <ActionButton onClick={props.onShowcaseResult} variant="secondary" icon={<SparklesIcon className="w-4 h-4" />} disabled={!props.smileRating || !props.afterSmileRating}>View Comparison</ActionButton>
                            <ActionButton onClick={props.onGenerateReport} variant="secondary" icon={props.isGeneratingReport ? <RefreshCwIcon className="w-4 h-4 animate-spin-slow" /> : <FileTextIcon className="w-4 h-4" />} disabled={props.isGeneratingReport || !props.treatmentPlan || !props.smileRating || !props.afterSmileRating}>Standard Report</ActionButton>
                            <ActionButton onClick={props.onGeneratePrestigeReport} variant="secondary" icon={props.isGeneratingPrestigeReport ? <RefreshCwIcon className="w-4 h-4 animate-spin-slow" /> : <FileTextIcon className="w-4 h-4" />} disabled={props.isGeneratingPrestigeReport || !props.smileRating || !props.afterSmileRating}>Premium Report</ActionButton>
                            {/* <ActionButton onClick={props.onGenerateEliteReport} variant="primary" icon={props.isGeneratingEliteReport ? <RefreshCwIcon className="w-4 h-4 animate-spin-slow" /> : <CrownIcon className="w-4 h-4" />} disabled={props.isGeneratingEliteReport || !props.smileRating || !props.afterSmileRating}>Executive Report</ActionButton> */}
                            <ActionButton onClick={props.onGenerateBrochure} variant="secondary" icon={props.isGeneratingBrochure ? <RefreshCwIcon className="w-4 h-4 animate-spin-slow" /> : <BookOpenIcon className="w-4 h-4" />} disabled={props.isGeneratingBrochure || !props.smileRating || !props.afterSmileRating}>Brochure (4-Page)</ActionButton>
                            <div className="pt-3 mt-2 border-t border-white/10">
                                <ActionButton onClick={() => setIsClinicSettingsOpen(true)} variant="tertiary" icon={<Settings2Icon className="w-4 h-4" />}>Clinic Settings</ActionButton>
                            </div>
                            {props.reportError && <p className="text-red-500 text-xs mt-2 text-center">{props.reportError}</p>}
                            {props.prestigeReportError && <p className="text-red-500 text-xs mt-2 text-center">{props.prestigeReportError}</p>}
                            {props.eliteReportError && <p className="text-red-500 text-xs mt-2 text-center">{props.eliteReportError}</p>}
                            {props.brochureError && <p className="text-red-500 text-xs mt-2 text-center">{props.brochureError}</p>}
                        </div>
                    </div>
                </AccordionSection>

                <AccordionSection title="Workflow" id="navigation">
                    <div className="space-y-1">
                        <ActionButton onClick={props.onGoBack} variant="tertiary" icon={<span className="material-symbols-outlined text-[18px]">tune</span>}>Edit Parameters</ActionButton>
                        <ActionButton onClick={props.onReset} variant="tertiary" icon={<ImageUpIcon className="w-4 h-4" />}>Start New Case</ActionButton>
                    </div>
                </AccordionSection>
            </div>
            <footer className="p-6 text-center text-zinc-600 text-xs border-t border-white/10">FuturaSmile &copy; {new Date().getFullYear()}</footer>

            {/* Clinic Settings Modal */}
            <ClinicSettingsModal isOpen={isClinicSettingsOpen} onClose={() => setIsClinicSettingsOpen(false)} />
        </aside >
    );
};

export default StudioPanel;
